import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { Plus, Search, Settings2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, inputCls, btnPrimary, btnSecondary, cardCls, EmptyState } from "@/components/shared";
import { PinLock } from "@/components/diary/PinLock";
import { EntryDialog } from "@/components/diary/EntryDialog";
import { DiarySettings } from "@/components/diary/DiarySettings";

const EntryCard = ({ entry, onOpen }) => {
  const d = parseISO(entry.entry_date);
  return (
    <button onClick={onOpen} data-testid={`diary-entry-${entry.id}`} className={`${cardCls} w-full text-left p-5 sm:p-6 flex gap-5 hover:-translate-y-0.5 fade-up`}>
      <div className="w-14 shrink-0 text-center border-r border-t-line pr-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-t-muted">{format(d, "MMM")}</p>
        <p className="font-serif-display text-3xl font-semibold leading-none text-t-primary">{format(d, "d")}</p>
        <p className="text-[10px] text-t-faint mt-1">{format(d, "yyyy")}</p>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-[0.2em] text-t-muted">{format(d, "EEEE")}</p>
        <h3 className="font-serif-display text-2xl font-semibold leading-tight truncate">{entry.title}</h3>
        {entry.prompt && entry.prompt !== entry.title && <p className="text-xs italic text-t-muted mt-1 truncate">{entry.prompt}</p>}
        <p className="text-sm text-t-c4 mt-2 line-clamp-2 whitespace-pre-wrap">{entry.content}</p>
      </div>
    </button>
  );
};

const Journal = ({ token, setToken }) => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null);
  const [settings, setSettings] = useState(false);
  const headers = useMemo(() => (token ? { "X-Diary-Token": token } : {}), [token]);

  const load = useCallback(async () => {
    try {
      setEntries((await api.get("/diary/entries", { params: { q }, headers })).data);
      setLoading(false);
    } catch (e) {
      if (e.response?.status === 423) setToken(null);
    }
  }, [q, headers, setToken]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="diary-page">
      <PageHeader eyebrow="Just for you" title="Diary" subtitle="A quiet place for your thoughts, feelings and little moments worth keeping."
        action={
          <div className="flex gap-2 flex-wrap">
            {user.diary_pin_enabled && <button data-testid="diary-lock-button" className={btnSecondary} onClick={() => setToken(null)}><Lock className="h-4 w-4" /> Lock</button>}
            <button data-testid="diary-settings-button" className={btnSecondary} onClick={() => setSettings(true)}><Settings2 className="h-4 w-4" /> Settings</button>
            <button data-testid="diary-new-entry-button" className={btnPrimary} onClick={() => setDialog({})}><Plus className="h-4 w-4" /> New entry</button>
          </div>
        } />
      <div className="relative max-w-md mb-8 fade-up">
        <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-t-faint" />
        <input data-testid="diary-search-input" className={`${inputCls} pl-11`} placeholder="Search your entries..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {entries.length ? (
        <div className="space-y-4 max-w-3xl" data-testid="diary-entries-list">
          {entries.map((e) => <EntryCard key={e.id} entry={e} onOpen={() => setDialog({ entry: e })} />)}
        </div>
      ) : (
        !loading && <EmptyState testId="diary-empty" title={q ? "No matches" : "Your diary is waiting"} text={q ? "Try a different word." : "Write your first entry — no rules, just you."} />
      )}
      <EntryDialog state={dialog} onClose={() => setDialog(null)} onSaved={load} headers={headers} />
      <DiarySettings open={settings} onClose={() => setSettings(false)} setToken={setToken} />
    </div>
  );
};

export default function Diary() {
  const { user } = useAuth();
  const [token, setToken] = useState(null);
  if (user.diary_pin_enabled && !token) return <PinLock onUnlock={setToken} />;
  return <Journal token={token} setToken={setToken} />;
}
